import React from "react";
import clsx from "clsx";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {useKeycloak} from "@react-keycloak/web";

import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import {Box, ThemeProvider, Typography} from "@material-ui/core";
import AppBar from "@material-ui/core/AppBar";
import Container from "@material-ui/core/Container";
import CssBaseline from "@material-ui/core/CssBaseline";
import Drawer from "@material-ui/core/Drawer";
import Divider from "@material-ui/core/Divider";
import IconButton from "@material-ui/core/IconButton";
import Toolbar from "@material-ui/core/Toolbar";

import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";

import SCCFaviconCOL from "../../graphics/logo/SCC_favicon_COL.png";
import {ProfileDTO} from "../../models/user";
import {API, createApiConfig} from "../../utils";
import {AxiosError, AxiosResponse} from "axios";

import {AdminMenu, MunicipalitiesMenu, PublicMenu, UserMenu} from "./menus";
import {TopMenu} from "./navbar";
import {THEMES} from "../../styles";
import {LoadingBackdrop} from "../common/Loading";
import {AppContextProvider} from "./context";

interface SCCompassAppProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    children: any;
}

interface SCCompassAppState {
    drawerOpen: boolean;
    lastWidth: number;
    loggedIn: boolean;
    profile: ProfileDTO | null;
    theme: "light" | "dark";
    name: "loading" | "loaded" | "failed";
}

const drawerWidth = 240;

function shouldOpenDrawer(width: number): boolean {
    return width - drawerWidth > 800;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        appBar: {
            transition: theme.transitions.create(["margin", "width"], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            backgroundColor: "#ffffff",
            color: "#111827",
            borderBottom: "1px solid #E5E7EB",
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        },
        appBarShift: {
            width: `calc(100% - ${drawerWidth}px)`,
            marginLeft: `${drawerWidth}px`,
            transition: theme.transitions.create(["margin", "width"], {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
            }),
        },
        appLogo: {
            height: "32px",
            width: "32px",
            marginRight: "10px",
            borderRadius: "6px",
        },
        appTitle: {
            flexGrow: 1,
            fontWeight: 700,
            fontSize: "1rem",
            color: "#111827",
            letterSpacing: "-0.01em",
        },
        mainDrawer: {
            width: drawerWidth,
            transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
            }),
            flexShrink: 0,
        },
        mainDrawerPaper: {
            width: drawerWidth,
            backgroundColor: "#ffffff",
            borderRight: "1px solid #E5E7EB",
        },
        mainDrawerHeader: {
            display: "flex",
            alignItems: "center",
            padding: theme.spacing(0, 1),
            ...theme.mixins.toolbar,
            justifyContent: "flex-start",
        },
        menuButton: {
            marginRight: theme.spacing(2),
            color: "#6B7280",
        },
        content: {
            flexGrow: 1,
            padding: theme.spacing(3),
            transition: theme.transitions.create("margin", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            marginLeft: -drawerWidth,
            minHeight: "100vh",
            backgroundColor: "#f9fafb",
        },
        contentShift: {
            transition: theme.transitions.create("margin", {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
            }),
            marginLeft: 0,
        },
        hidden: {
            display: "none",
        },
        drawerCloseButton: {
            color: "#6B7280",
        },
    })
);
export const SCCompassApp: React.FC<SCCompassAppProps> = (props: SCCompassAppProps) => {
    //== Init =================================================================
    const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
    const classes = useStyles();
    const {keycloak, initialized} = useKeycloak();
    const initState: SCCompassAppState = {
        drawerOpen: false,
        lastWidth: -1,
        loggedIn: keycloak.authenticated ?? false,
        profile: null,
        theme: prefersDarkMode ? "dark" : "light",
        name: "loading",
    };
    const [state, setState] = React.useState(initState);
    const [keycloakWaiting, setKeycloakWaiting] = React.useState("init" as "init" | "waiting" | "done");
    //== Effects ==============================================================
    React.useEffect(() => {
        if (keycloakWaiting === "init") {
            setTimeout(() => {
                setKeycloakWaiting("done");
            }, 8000);
            setKeycloakWaiting("waiting");
        } else if (keycloakWaiting === "done" && !initialized && state.name === "loading") {
            console.log("failed to load keycloak - treating user as anonymous");
            setState({...state, name: "loaded"});
        }
    }, [state, initialized, keycloakWaiting, setKeycloakWaiting]);
    React.useEffect(() => {
        window.addEventListener("resize", handleWindowResize);
        console.log("[SCCompassApp-layout] useEffect: started");
        if (state.name === "loading" && initialized) {
            if (keycloak.authenticated) {
                API.get<ProfileDTO>("/profile", createApiConfig(keycloak, initialized))
                    .then((res: AxiosResponse<ProfileDTO>) => {
                        setState({...state, profile: res.data, name: "loaded"});
                    })
                    .catch((err: AxiosError) => {
                        setState({...state, name: "failed"});
                    });
            } else {
                setState({...state, name: "loaded"});
            }
        }
        if (keycloak.authenticated && !state.loggedIn) {
            setState({...state, loggedIn: true, name: "loading"});
        }
        if (window.innerWidth === -1) {
            setState({...state, lastWidth: window.innerWidth});
        }
        return () => {
            window.removeEventListener("resize", handleWindowResize);
        };
    }, [state, setState, keycloak, initialized]);
    //== Handlers =============================================================
    const handleDrawerOpen = (): void => {
        setState({...state, drawerOpen: true});
    };
    const handleDrawerClose = (): void => {
        setState({...state, drawerOpen: false});
    };
    const handleThemeChange = (theme: "light" | "dark"): void => {
        setState({...state, theme: theme});
    };
    const handleWindowResize = (): void => {
        if (window.innerWidth !== state.lastWidth) {
            setState({...state, lastWidth: window.innerWidth});
        }
    };
    //== Render ===============================================================
    const isDrawerOpen = state.drawerOpen || shouldOpenDrawer(state.lastWidth);
    return (
        <ThemeProvider theme={THEMES[state.theme]}>
            <Box style={{display: "flex"}}>
                <CssBaseline />
                <AppBar
                    position="fixed"
                    className={clsx(classes.appBar, {
                        [classes.appBarShift]: isDrawerOpen,
                    })}
                    elevation={0}
                >
                    <Toolbar>
                        <IconButton
                            aria-label="open drawer"
                            onClick={handleDrawerOpen}
                            edge="start"
                            className={clsx(classes.menuButton, isDrawerOpen && classes.hidden)}
                            disabled={shouldOpenDrawer(state.lastWidth)}
                        >
                            <MenuIcon />
                        </IconButton>
                        <img src={SCCFaviconCOL} alt="logo" className={classes.appLogo} />
                        <Typography variant="h6" className={classes.appTitle}>
                            Smart City Compass
                        </Typography>
                        <TopMenu activeTheme={state.theme} changeTheme={handleThemeChange} />
                    </Toolbar>
                </AppBar>
                <Drawer
                    className={classes.mainDrawer}
                    variant="persistent"
                    anchor="left"
                    open={isDrawerOpen}
                    classes={{
                        paper: classes.mainDrawerPaper,
                    }}
                >
                    <div className={classes.mainDrawerHeader}>
                        <IconButton
                            onClick={handleDrawerClose}
                            disabled={shouldOpenDrawer(state.lastWidth)}
                            className={classes.drawerCloseButton}
                        >
                            <ChevronLeftIcon />
                        </IconButton>
                    </div>
                    <Divider />
                    <PublicMenu />
                    <UserMenu />
                    <MunicipalitiesMenu />
                    <AdminMenu />
                </Drawer>
                {state.name === "loading" && <LoadingBackdrop />}
                {state.name === "loaded" && (
                    <AppContextProvider profile={state.profile} drawer={isDrawerOpen}>
                        <main className={clsx(classes.content, {[classes.contentShift]: isDrawerOpen})}>
                            <div className={classes.mainDrawerHeader} />
                            <Container maxWidth="xl">{props.children}</Container>
                        </main>
                    </AppContextProvider>
                )}
            </Box>
        </ThemeProvider>
    );
};

export const SCCompassSimpleLayout: React.FC<SCCompassAppProps> = (props: SCCompassAppProps) => {
    //== Init =================================================================
    const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
    const initState: {theme: "light" | "dark"} = {
        theme: prefersDarkMode ? "dark" : "light",
    };
    //== Render ===============================================================
    return (
        <ThemeProvider theme={THEMES[initState.theme]}>
            <CssBaseline />
            <main>{props.children}</main>
        </ThemeProvider>
    );
};

export default SCCompassApp;
